import { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from '@pantry/ui';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { logger } from '../../lib/logger';

interface WebBarcodeScannerProps {
  onScan: (barcode: string) => void;
  onClose: () => void;
}

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
];

/**
 * Web barcode scanner component using html5-qrcode library.
 * Opens a modal with webcam view for scanning product barcodes.
 */
export function WebBarcodeScanner({ onScan, onClose }: WebBarcodeScannerProps) {
  const { t } = useLingui();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  // Stable references to avoid stale closures
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);

  // Update refs in useLayoutEffect to keep callbacks fresh
  useLayoutEffect(() => {
    onScanRef.current = onScan;
    onCloseRef.current = onClose;
  });

  // Stop camera and release all resources
  const stopCamera = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;

    // Clear ref immediately to prevent double-stop
    scannerRef.current = null;

    try {
      const state = scanner.getState();
      // State: 1 = NOT_STARTED, 2 = SCANNING, 3 = PAUSED
      if (state === 2 || state === 3) {
        await scanner.stop();
      }
    } catch (err) {
      logger.debug('Error stopping scanner', { error: String(err) });
    }

    try {
      scanner.clear();
    } catch (err) {
      logger.debug('Error clearing scanner', { error: String(err) });
    }
  }, []);

  useEffect(() => {
    const readerId = 'web-barcode-reader';
    let isCancelled = false;
    let localScanner: Html5Qrcode | null = null;

    const initScanner = async () => {
      // Wait a tick for the DOM element to be ready
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (isCancelled) return;

      // Clear any existing content in the reader element
      const readerElement = document.getElementById(readerId);
      if (readerElement) {
        readerElement.innerHTML = '';
      }

      // Create scanner instance
      const scanner = new Html5Qrcode(readerId, {
        formatsToSupport: SUPPORTED_FORMATS,
        verbose: false,
      });
      localScanner = scanner;

      if (isCancelled) {
        // Cleanup if cancelled during creation
        try {
          scanner.clear();
        } catch {
          // Ignore
        }
        return;
      }

      scannerRef.current = scanner;

      try {
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 280, height: 180 },
            aspectRatio: 1.5,
          },
          (decodedText: string) => {
            if (isCancelled) return;

            // Stop scanning and notify parent
            scanner
              .stop()
              .then(() => scanner.clear())
              .catch(() => {})
              .finally(() => {
                scannerRef.current = null;
                onScanRef.current(decodedText);
              });
          },
          () => {
            // Scan error - ignore, keep scanning
          }
        );
      } catch (err) {
        logger.error('Failed to start scanner', err);
        if (!isCancelled) {
          setError(
            err instanceof Error
              ? err.message
              : t`Failed to access camera. Please ensure camera permissions are granted.`
          );
        }
      }
    };

    initScanner();

    // Cleanup function
    return () => {
      isCancelled = true;

      const scanner = localScanner || scannerRef.current;
      scannerRef.current = null;

      if (scanner) {
        // Use IIFE to handle async cleanup
        (async () => {
          try {
            const state = scanner.getState();
            if (state === 2 || state === 3) {
              await scanner.stop();
            }
          } catch {
            // Ignore stop errors
          }
          try {
            scanner.clear();
          } catch {
            // Ignore clear errors
          }
        })();
      }
    };
  }, []);

  const handleClose = useCallback(async () => {
    if (isClosing) return;
    setIsClosing(true);

    await stopCamera();
    onCloseRef.current();
  }, [isClosing, stopCamera]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80">
      <div className="relative w-full max-w-md mx-4 bg-card rounded-lg overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold">
            <Trans>Scan Barcode</Trans>
          </h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={handleClose}
            disabled={isClosing}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        {/* Scanner Container */}
        <div className="relative bg-black">
          <div id="web-barcode-reader" className="w-full" />
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 text-center">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Instructions */}
        {!error && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            <p>
              <Trans>Position the barcode within the frame</Trans>
            </p>
            <p className="mt-1">
              <Trans>Supports EAN-13, EAN-8, UPC-A, UPC-E, Code128, Code39</Trans>
            </p>
          </div>
        )}

        {/* Cancel Button */}
        <div className="p-4 pt-0">
          <Button variant="outline" className="w-full" onClick={handleClose} disabled={isClosing}>
            {isClosing ? <Trans>Closing...</Trans> : <Trans>Cancel</Trans>}
          </Button>
        </div>
      </div>
    </div>
  );
}
